/**
 * WeaveIn.ai Platform - Organization Branding API Service
 * TypeScript service layer for organization branding management
 */

import axios, { AxiosInstance } from 'axios';
import { config as productConfig } from '@/src/config';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface OrganizationBranding {
  id: number;
  organization_id: number;

  // Email branding
  email_from_name?: string;
  email_from_email?: string;
  email_reply_to?: string;
  email_header_logo_url?: string;
  email_header_background_color?: string;
  email_footer_html?: string;
  email_footer_text?: string;
  email_primary_color?: string;
  email_secondary_color?: string;
  email_text_color?: string;
  email_background_color?: string;
  email_signature_template?: string;
  email_custom_css?: string;
  email_domain?: string;
  email_domain_verified?: boolean;
  email_dkim_configured?: boolean;
  email_spf_configured?: boolean;

  // Social media branding
  social_display_name?: string;
  social_bio_template?: string;
  social_profile_image_url?: string;
  social_cover_image_url?: string;
  social_default_hashtags?: string[];
  social_disclaimer_text?: string;
  social_link_in_bio_url?: string;
  social_watermark_image_url?: string;
  social_watermark_position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
  social_watermark_opacity?: number;
  social_handle_facebook?: string;
  social_handle_instagram?: string;
  social_handle_twitter?: string;
  social_handle_linkedin?: string;
  social_handle_bluesky?: string;

  // General branding
  brand_name?: string;
  brand_tagline?: string;
  brand_primary_color?: string;
  brand_secondary_color?: string;
  brand_accent_color?: string;
  brand_text_color?: string;
  brand_background_color?: string;
  brand_logo_url?: string;
  brand_logo_dark_url?: string;
  brand_favicon_url?: string;
  brand_font_family?: string;
  brand_font_family_heading?: string;
  brand_sidebar_color?: string;

  // Document branding
  document_header_logo_url?: string;
  document_footer_text?: string;
  document_letterhead_url?: string;
  document_watermark_url?: string;

  // Communication branding
  sms_sender_name?: string;
  sms_signature?: string;
  voice_greeting?: string;
  voice_hold_message?: string;
  voice_voicemail_greeting?: string;

  // Portal branding
  portal_welcome_message?: string;
  portal_custom_css?: string;
  portal_banner_image_url?: string;

  // Metadata
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  updated_by?: number;

  // Sandbox flags
  is_sandbox?: boolean;
  sandbox_expires_at?: string | null;
}

export interface OrganizationBrandingCreate {
  organization_id: number;
  email_from_name?: string;
  email_from_email?: string;
  email_reply_to?: string;
  email_header_logo_url?: string;
  email_header_background_color?: string;
  email_footer_html?: string;
  email_footer_text?: string;
  email_primary_color?: string;
  email_secondary_color?: string;
  email_text_color?: string;
  email_background_color?: string;
  email_signature_template?: string;
  email_custom_css?: string;
  email_domain?: string;
  social_display_name?: string;
  social_bio_template?: string;
  social_profile_image_url?: string;
  social_cover_image_url?: string;
  social_default_hashtags?: string[];
  social_disclaimer_text?: string;
  social_link_in_bio_url?: string;
  social_watermark_image_url?: string;
  social_watermark_position?: string;
  social_watermark_opacity?: number;
  social_handle_facebook?: string;
  social_handle_instagram?: string;
  social_handle_twitter?: string;
  social_handle_linkedin?: string;
  social_handle_bluesky?: string;
  brand_name?: string;
  brand_tagline?: string;
  brand_primary_color?: string;
  brand_secondary_color?: string;
  brand_accent_color?: string;
  brand_text_color?: string;
  brand_background_color?: string;
  brand_logo_url?: string;
  brand_logo_dark_url?: string;
  brand_favicon_url?: string;
  brand_font_family?: string;
  brand_font_family_heading?: string;
  brand_sidebar_color?: string;
  document_header_logo_url?: string;
  document_footer_text?: string;
  document_letterhead_url?: string;
  document_watermark_url?: string;
  sms_sender_name?: string;
  sms_signature?: string;
  voice_greeting?: string;
  voice_hold_message?: string;
  voice_voicemail_greeting?: string;
  portal_welcome_message?: string;
  portal_custom_css?: string;
  portal_banner_image_url?: string;
}

export interface EmailTemplate {
  id: number;
  organization_id: number;
  branding_id?: number;
  template_name: string;
  template_type: 'transactional' | 'marketing' | 'newsletter' | 'outreach' | 'notification' | 'welcome' | 'reminder';
  template_description?: string;
  html_template: string;
  text_template?: string;
  is_default: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
}

export interface EmailTemplateCreate {
  template_name: string;
  template_type: 'transactional' | 'marketing' | 'newsletter' | 'outreach' | 'notification' | 'welcome' | 'reminder';
  template_description?: string;
  html_template: string;
  text_template?: string;
  is_default?: boolean;
  is_active?: boolean;
}

export interface BrandingAsset {
  id: number;
  organization_id: number;
  asset_type: 'logo' | 'logo_dark' | 'favicon' | 'email_header' | 'email_footer' | 'social_profile' | 'social_cover' | 'social_watermark' | 'document_letterhead' | 'document_watermark' | 'portal_banner';
  asset_name: string;
  asset_description?: string;
  file_url: string;
  file_path?: string;
  file_size?: number;
  mime_type?: string;
  width?: number;
  height?: number;
  is_active: boolean;
  is_primary: boolean;
  created_at?: string;
  updated_at?: string;
  uploaded_by?: number;
}

export interface EmailPreviewRequest {
  template_type?: string;
  subject?: string;
  content?: string;
  recipient_name?: string;
  cta_text?: string;
  cta_url?: string;
}

export interface EmailPreviewResponse {
  html: string;
  text: string;
  subject: string;
}

// ============================================================================
// API CLIENT
// ============================================================================

class BrandingAPI {
  private client: AxiosInstance;

  // Default to the product-aware main API URL — ms-api-hots on Triveny,
  // weavein-api on WeaveIn. Previously this read VITE_WEAVEIN_API_URL
  // directly, which on Triveny Vercel pointed at weavein-api whose
  // get_actor dual-auth bridge is only enabled on PRODUCT_PROFILE=
  // ms_standalone → every platform JWT 401'd as "Invalid token" and
  // the Branding tab rendered "Failed to load branding settings".
  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Only fall back to the active org when the call didn't name one. Every
      // org-scoped method passes X-CBO-ID explicitly, and overwriting it here
      // made Organization Settings > Branding for org N read — and SAVE —
      // the admin's active org (e.g. CBO 1) instead of N.
      if (cboId && !config.headers['X-CBO-ID']) {
        config.headers['X-CBO-ID'] = cboId;
      }

      return config;
    });
  }

  // ========================================================================
  // BRANDING SETTINGS ENDPOINTS
  // ========================================================================

  /**
   * Get organization branding settings
   * Note: organizationId is passed for reference but the X-CBO-ID header (set by interceptor) determines the org
   */
  async getBranding(organizationId: number): Promise<OrganizationBranding | null> {
    try {
      const response = await this.client.get(`/api/organization-branding`, {
        headers: { 'X-CBO-ID': String(organizationId) }
      });
      // If backend returns empty defaults (id is null), treat as no branding
      if (!response.data?.id) {
        return null;
      }
      // Transform nested response to flat structure for frontend
      return transformFromNestedBranding(response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Create organization branding settings
   */
  async createBranding(data: OrganizationBrandingCreate): Promise<OrganizationBranding> {
    const nestedData = transformToNestedBranding(data);

    const response = await this.client.post('/api/organization-branding', nestedData, {
      headers: { 'X-CBO-ID': String(data.organization_id) }
    });
    // Transform nested response to flat structure for frontend
    return transformFromNestedBranding(response.data);
  }

  /**
   * Update organization branding settings
   */
  async updateBranding(
    organizationId: number,
    data: Partial<OrganizationBrandingCreate>
  ): Promise<OrganizationBranding> {
    const nestedData = transformToNestedBranding(data);

    const response = await this.client.put(`/api/organization-branding`, nestedData, {
      headers: { 'X-CBO-ID': String(organizationId) }
    });
    // Transform nested response to flat structure for frontend
    return transformFromNestedBranding(response.data);
  }

  /**
   * Delete organization branding settings
   */
  async deleteBranding(organizationId: number): Promise<void> {
    await this.client.delete(`/api/organization-branding`, {
      headers: { 'X-CBO-ID': String(organizationId) }
    });
  }

  // ========================================================================
  // EMAIL TEMPLATE ENDPOINTS
  // ========================================================================

  /**
   * List email templates for organization
   */
  async listEmailTemplates(organizationId: number): Promise<EmailTemplate[]> {
    const response = await this.client.get<EmailTemplate[]>(
      `/api/organization-branding/email-templates`,
      { headers: { 'X-CBO-ID': String(organizationId) } }
    );
    return response.data;
  }

  /**
   * Get a single email template
   */
  async getEmailTemplate(templateId: number): Promise<EmailTemplate> {
    const response = await this.client.get<EmailTemplate>(
      `/api/organization-branding/email-templates/${templateId}`
    );
    return response.data;
  }

  /**
   * Create email template
   */
  async createEmailTemplate(
    organizationId: number,
    data: EmailTemplateCreate
  ): Promise<EmailTemplate> {
    const response = await this.client.post<EmailTemplate>(
      `/api/organization-branding/email-templates`,
      data,
      { headers: { 'X-CBO-ID': String(organizationId) } }
    );
    return response.data;
  }

  /**
   * Update email template
   */
  async updateEmailTemplate(
    templateId: number,
    data: Partial<EmailTemplateCreate>
  ): Promise<EmailTemplate> {
    const response = await this.client.put<EmailTemplate>(
      `/api/organization-branding/email-templates/${templateId}`,
      data
    );
    return response.data;
  }

  /**
   * Delete email template
   */
  async deleteEmailTemplate(templateId: number): Promise<void> {
    await this.client.delete(`/api/organization-branding/email-templates/${templateId}`);
  }

  // ========================================================================
  // ASSET ENDPOINTS
  // ========================================================================

  /**
   * List branding assets for organization
   */
  async listAssets(organizationId: number, assetType?: string): Promise<BrandingAsset[]> {
    let url = `/api/organization-branding/assets`;
    if (assetType) {
      url += `?asset_type=${assetType}`;
    }
    const response = await this.client.get<BrandingAsset[]>(url, {
      headers: { 'X-CBO-ID': String(organizationId) }
    });
    return response.data;
  }

  /**
   * Upload branding asset
   */
  async uploadAsset(
    organizationId: number,
    assetType: string,
    file: File,
    assetName?: string
  ): Promise<BrandingAsset> {
    const formData = new FormData();
    formData.append('file', file);
    if (assetName) {
      formData.append('asset_name', assetName);
    }

    const params = new URLSearchParams();
    params.append('asset_type', assetType);
    if (assetName) {
      params.append('asset_name', assetName);
    }

    const response = await this.client.post<BrandingAsset>(
      `/api/organization-branding/assets?${params.toString()}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CBO-ID': String(organizationId),
        },
      }
    );
    return response.data;
  }

  /**
   * Delete branding asset
   */
  async deleteAsset(assetId: number): Promise<void> {
    await this.client.delete(`/api/organization-branding/assets/${assetId}`);
  }

  /**
   * Set asset as primary
   */
  async setAssetPrimary(assetId: number): Promise<BrandingAsset> {
    const response = await this.client.put<BrandingAsset>(
      `/api/organization-branding/assets/${assetId}/primary`
    );
    return response.data;
  }

  // ========================================================================
  // PREVIEW ENDPOINTS
  // ========================================================================

  /**
   * Preview branded email
   */
  async previewEmail(
    organizationId: number,
    data: EmailPreviewRequest
  ): Promise<EmailPreviewResponse> {
    const params = new URLSearchParams();
    if (data.template_type) params.append('template_type', data.template_type);

    const response = await this.client.get<EmailPreviewResponse>(
      `/api/organization-branding/preview-email?${params.toString()}`
    );
    return response.data;
  }
}

// Export singleton instance - uses env var or empty string for proxy routing
export const brandingAPI = new BrandingAPI();

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Transform nested branding response from backend to flat structure for frontend
 */
function transformFromNestedBranding(data: any): OrganizationBranding {
  const email = data.email || {};
  const social = data.social_media || {};
  const general = data.general || {};
  const document = data.document || {};
  const communication = data.communication || {};
  const portal = data.portal || {};

  return {
    id: data.id,
    organization_id: data.organization_id,
    // Email branding
    email_from_name: email.from_name,
    email_from_email: email.from_email,
    email_reply_to: email.reply_to,
    email_header_logo_url: email.header_logo_url,
    email_header_background_color: email.header_background_color,
    email_footer_html: email.footer_html,
    email_footer_text: email.footer_text,
    email_primary_color: email.primary_color,
    email_secondary_color: email.secondary_color,
    email_text_color: email.text_color,
    email_background_color: email.background_color,
    email_signature_template: email.signature_template,
    email_custom_css: email.custom_css,
    email_domain: email.domain,
    email_domain_verified: email.domain_verified,
    email_dkim_configured: email.dkim_configured,
    email_spf_configured: email.spf_configured,
    // Social media branding
    social_display_name: social.display_name,
    social_bio_template: social.bio_template,
    social_profile_image_url: social.profile_image_url,
    social_cover_image_url: social.cover_image_url,
    social_default_hashtags: social.default_hashtags,
    social_disclaimer_text: social.disclaimer_text,
    social_link_in_bio_url: social.link_in_bio_url,
    social_watermark_image_url: social.watermark_image_url,
    social_watermark_position: social.watermark_position,
    social_watermark_opacity: social.watermark_opacity,
    social_handle_facebook: social.handle_facebook,
    social_handle_instagram: social.handle_instagram,
    social_handle_twitter: social.handle_twitter,
    social_handle_linkedin: social.handle_linkedin,
    social_handle_bluesky: social.handle_bluesky,
    // General branding
    brand_name: general.brand_name,
    brand_tagline: general.brand_tagline,
    brand_primary_color: general.primary_color,
    brand_secondary_color: general.secondary_color,
    brand_accent_color: general.accent_color,
    brand_text_color: general.text_color,
    brand_background_color: general.background_color,
    brand_logo_url: general.logo_url,
    brand_logo_dark_url: general.logo_dark_url,
    brand_favicon_url: general.favicon_url,
    brand_font_family: general.font_family,
    brand_font_family_heading: general.font_family_heading,
    brand_sidebar_color: general.sidebar_color,
    // Document branding
    document_header_logo_url: document.header_logo_url,
    document_footer_text: document.footer_text,
    document_letterhead_url: document.letterhead_url,
    document_watermark_url: document.watermark_url,
    // Communication branding
    sms_sender_name: communication.sms_sender_name,
    sms_signature: communication.sms_signature,
    voice_greeting: communication.voice_greeting,
    voice_hold_message: communication.voice_hold_message,
    voice_voicemail_greeting: communication.voice_voicemail_greeting,
    // Portal branding
    portal_welcome_message: portal.welcome_message,
    portal_custom_css: portal.custom_css,
    portal_banner_image_url: portal.banner_image_url,
    // Metadata
    is_active: data.is_active,
    created_at: data.created_at,
    updated_at: data.updated_at,
    created_by: data.created_by,
    updated_by: data.updated_by,
    // Sandbox flags
    is_sandbox: data.is_sandbox || false,
    sandbox_expires_at: data.sandbox_expires_at || null,
  };
}

/**
 * Transform flat branding data to nested structure expected by backend
 */
function transformToNestedBranding(data: Partial<OrganizationBrandingCreate>): object {
  return {
    email: {
      from_name: data.email_from_name,
      from_email: data.email_from_email,
      reply_to: data.email_reply_to,
      header_logo_url: data.email_header_logo_url,
      header_background_color: data.email_header_background_color,
      footer_html: data.email_footer_html,
      footer_text: data.email_footer_text,
      primary_color: data.email_primary_color,
      secondary_color: data.email_secondary_color,
      text_color: data.email_text_color,
      background_color: data.email_background_color,
      signature_template: data.email_signature_template,
      custom_css: data.email_custom_css,
      domain: data.email_domain,
    },
    social_media: {
      display_name: data.social_display_name,
      bio_template: data.social_bio_template,
      profile_image_url: data.social_profile_image_url,
      cover_image_url: data.social_cover_image_url,
      default_hashtags: data.social_default_hashtags,
      disclaimer_text: data.social_disclaimer_text,
      link_in_bio_url: data.social_link_in_bio_url,
      watermark_image_url: data.social_watermark_image_url,
      watermark_position: data.social_watermark_position,
      watermark_opacity: data.social_watermark_opacity,
      handle_facebook: data.social_handle_facebook,
      handle_instagram: data.social_handle_instagram,
      handle_twitter: data.social_handle_twitter,
      handle_linkedin: data.social_handle_linkedin,
      handle_bluesky: data.social_handle_bluesky,
    },
    general: {
      brand_name: data.brand_name,
      brand_tagline: data.brand_tagline,
      primary_color: data.brand_primary_color,
      secondary_color: data.brand_secondary_color,
      accent_color: data.brand_accent_color,
      text_color: data.brand_text_color,
      background_color: data.brand_background_color,
      logo_url: data.brand_logo_url,
      logo_dark_url: data.brand_logo_dark_url,
      favicon_url: data.brand_favicon_url,
      font_family: data.brand_font_family,
      font_family_heading: data.brand_font_family_heading,
      sidebar_color: data.brand_sidebar_color,
    },
    document: {
      header_logo_url: data.document_header_logo_url,
      footer_text: data.document_footer_text,
      letterhead_url: data.document_letterhead_url,
      watermark_url: data.document_watermark_url,
    },
    communication: {
      sms_sender_name: data.sms_sender_name,
      sms_signature: data.sms_signature,
      voice_greeting: data.voice_greeting,
      voice_hold_message: data.voice_hold_message,
      voice_voicemail_greeting: data.voice_voicemail_greeting,
    },
    portal: {
      welcome_message: data.portal_welcome_message,
      custom_css: data.portal_custom_css,
      banner_image_url: data.portal_banner_image_url,
    },
  };
}

/**
 * Get default branding values
 */
export const getDefaultBranding = (): Partial<OrganizationBranding> => ({
  brand_primary_color: '#2563EB',
  brand_secondary_color: '#1E40AF',
  brand_accent_color: '#3B82F6',
  brand_text_color: '#1F2937',
  brand_background_color: '#FFFFFF',
  email_primary_color: '#2563EB',
  email_text_color: '#1F2937',
  email_background_color: '#FFFFFF',
  email_header_background_color: '#2563EB',
  social_watermark_position: 'bottom-right',
  social_watermark_opacity: 0.7,
  brand_font_family: 'Inter, system-ui, sans-serif',
  brand_font_family_heading: 'Inter, system-ui, sans-serif',
});

/**
 * Validate hex color format
 */
export const isValidHexColor = (color: string): boolean => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
};

/**
 * Validate email address format
 */
export const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Get contrasting text color for a background
 */
export const getContrastingTextColor = (bgColor: string): string => {
  // Remove # if present
  const hex = bgColor.replace('#', '');

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};
